/**
 * HI-SI Dashboard - Futuristic Loading Screen Component
 */

import React from 'react';
import { Cpu, Zap, Activity } from 'lucide-react';
import { useDashboardServerStore } from '@/stores/dashboard-server';
import AtmosphericBackground from '@/components/futuristic/AtmosphericBackground';
import MosaicLoader from '@/components/ui/MosaicLoader';
import '@/styles/futuristic.css';

// CSS-in-JS styles for interactive elements using tokens
const loadingScreenStyles = `
  .pipeline-stage-badge:hover {
    background-color: color-mix(in srgb, var(--accent) 10%, transparent) !important;
    border-color: color-mix(in srgb, var(--accent) 30%, transparent) !important;
  }
`;

// Inject styles once
if (typeof document !== 'undefined' && !document.getElementById('loading-screen-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'loading-screen-styles';
  styleElement.textContent = loadingScreenStyles;
  document.head.appendChild(styleElement);
}

const LoadingScreen: React.FC = () => {
  const { connectionStatus } = useDashboardServerStore();
  
  const getMessage = () => {
    switch (connectionStatus) {
      case 'disconnected':
        return 'Connecting to HI-SI Dashboard...';
      case 'reconnecting':
        return 'Reconnecting to server...';
      default:
        return 'Loading HI-SI Dashboard...';
    }
  };

  return (
    <div className="void-foundation min-h-screen flex items-center justify-center">
      <AtmosphericBackground />
      
      <div className="relative z-10 text-center">
        {/* Logo and Branding */}
        <div className="mb-8">
          {/* Mosaic Loader */}
          <div className="relative w-64 h-48 mx-auto mb-6 flex items-center justify-center">
            <MosaicLoader
              density="normal"
              variant="agi-cyberpunk"
              size="normal"
              spacing="normal"
              seed="hisi-loading"
              loopDepth={true}
            />
          </div>
          
          <h1 className="text-5xl font-light text-gradient-cyan tracking-wider mb-2 animate-fadeIn">
            COMMAND CENTER
          </h1>
          <p 
            className="font-mono tracking-wider"
            style={{
              fontSize: 'var(--font-size-lg)',
              color: 'color-mix(in srgb, var(--fg-primary) 50%, transparent)',
              fontFamily: 'var(--font-family-mono)'
            }}
          >
            INITIALIZING SYSTEMS
          </p>
        </div>
        
        {/* Loading Animation */}
        <div className="mb-6">
          <div 
            className="flex items-center justify-center"
            style={{ gap: 'var(--space-2)' }}
          >
            <div 
              className="rounded-full animate-pulse"
              style={{
                width: 'var(--space-3)',
                height: 'var(--space-3)',
                backgroundColor: 'var(--accent)',
                animationDelay: '0'
              }}
            />
            <div 
              className="rounded-full animate-pulse"
              style={{
                width: 'var(--space-3)',
                height: 'var(--space-3)',
                backgroundColor: 'var(--accent)',
                animationDelay: 'var(--motion-duration-fast)'
              }}
            />
            <div 
              className="rounded-full animate-pulse"
              style={{
                width: 'var(--space-3)',
                height: 'var(--space-3)',
                backgroundColor: 'var(--accent)',
                animationDelay: 'calc(2 * var(--motion-duration-fast))'
              }}
            />
          </div>
        </div>
        
        {/* Status Message */}
        <p 
          className="font-mono"
          style={{
            color: 'color-mix(in srgb, var(--fg-primary) 70%, transparent)',
            fontSize: 'var(--font-size-lg)',
            marginBottom: 'var(--space-8)',
            fontFamily: 'var(--font-family-mono)'
          }}
        >
          {getMessage().toUpperCase()}
        </p>
        
        {/* Connection Status Indicator */}
        <div 
          className="flex items-center justify-center font-mono"
          style={{
            gap: 'var(--space-2)',
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-family-mono)'
          }}
        >
          <div 
            className="rounded-full"
            style={{
              width: 'var(--space-2)',
              height: 'var(--space-2)',
              backgroundColor: connectionStatus === 'connected' ? 'var(--success)' :
                             connectionStatus === 'reconnecting' ? 'var(--warning)' :
                             'var(--fg-muted)',
              boxShadow: connectionStatus === 'connected' ? '0 0 20px color-mix(in srgb, var(--success) 50%, transparent)' :
                        connectionStatus === 'reconnecting' ? '0 0 20px color-mix(in srgb, var(--warning) 50%, transparent)' :
                        'none',
              ...(connectionStatus === 'reconnecting' && { 
                animation: `pulse var(--motion-duration-slow) infinite` 
              })
            }}
          />
          <span 
            className="uppercase"
            style={{
              color: connectionStatus === 'connected' ? 'var(--success)' :
                    connectionStatus === 'reconnecting' ? 'var(--warning)' :
                    'color-mix(in srgb, var(--fg-primary) 50%, transparent)'
            }}
          >
            {connectionStatus}
          </span>
        </div>
        
        {/* Pipeline Stages Preview */}
        <div 
          className="max-w-4xl mx-auto"
          style={{
            marginTop: 'var(--space-12)',
            paddingLeft: 'var(--space-8)',
            paddingRight: 'var(--space-8)'
          }}
        >
          <p 
            className="font-mono uppercase tracking-wider"
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'color-mix(in srgb, var(--fg-primary) 40%, transparent)',
              marginBottom: 'var(--space-6)',
              fontFamily: 'var(--font-family-mono)'
            }}
          >
            Creative Pipeline Stages
          </p>
          <div 
            className="flex items-center justify-center flex-wrap"
            style={{ gap: 'var(--space-3)' }}
          >
            {['Script', 'Vision', 'Storyboard', 'Shots', 'Continuity', 'Frames', 'Video'].map((stage, index) => (
              <React.Fragment key={stage}>
                <div 
                  className="backdrop-blur-md font-mono uppercase transition-all pipeline-stage-badge"
                  style={{
                    padding: 'var(--space-3) var(--space-3)',
                    backgroundColor: 'color-mix(in srgb, var(--bg-surface) 30%, transparent)',
                    border: `1px solid color-mix(in srgb, var(--accent) 20%, transparent)`,
                    borderRadius: 'var(--radius-lg)',
                    color: 'color-mix(in srgb, var(--accent) 70%, transparent)',
                    fontFamily: 'var(--font-family-mono)',
                    fontSize: 'var(--font-size-xs)',
                    animation: `fadeIn var(--motion-duration-normal) var(--motion-ease-standard) ${index * 0.1}s both`
                  }}
                >
                  {stage}
                </div>
                {index < 6 && (
                  <Activity 
                    style={{
                      width: 'var(--space-3)',
                      height: 'var(--space-3)',
                      color: 'color-mix(in srgb, var(--accent) 30%, transparent)',
                      animation: `fadeIn var(--motion-duration-normal) var(--motion-ease-standard) ${index * 0.1 + 0.05}s both`
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        
        {/* Troubleshooting */}
        {connectionStatus === 'disconnected' && (
          <div 
            className="alert alert-warning backdrop-blur-md max-w-md mx-auto"
            style={{ marginTop: 'var(--space-8)' }}
          >
            <p 
              className="font-mono"
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--warning)',
                fontFamily: 'var(--font-family-mono)'
              }}
            >
              <strong 
                style={{ 
                  color: 'color-mix(in srgb, var(--warning) 85%, white)' 
                }}
              >
                CONNECTION ALERT
              </strong>
              <br />
              <span 
                style={{ 
                  fontSize: 'var(--font-size-xs)' 
                }}
              >
                VERIFY BACKEND SERVICE ON PORT 8080
              </span>
            </p>
          </div>
        )}
        
        {/* System Status */}
        <div 
          className="flex items-center justify-center font-mono"
          style={{
            marginTop: 'var(--space-8)',
            gap: 'var(--space-6)',
            fontSize: 'var(--font-size-xs)',
            fontFamily: 'var(--font-family-mono)',
            color: 'color-mix(in srgb, var(--fg-primary) 30%, transparent)'
          }}
        >
          <div className="flex items-center gap-2">
            <Zap className="w-3 h-3" />
            <span>QUANTUM CORE</span>
          </div>
          <div className="flex items-center gap-2">
            <Activity className="w-3 h-3" />
            <span>NEURAL MATRIX</span>
          </div>
          <div className="flex items-center gap-2">
            <Cpu className="w-3 h-3" />
            <span>SYNTHESIS ENGINE</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;